import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MEDIA } from "@/lib/mediaConfig";
import RotatingImage from "@/components/landing/RotatingImage";
import { ctaClick } from "@/lib/analytics";
import { useIsMobile } from "@/hooks/useIsMobile";

export default function Hero({ onDemoClick }) {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", isMobile ? "0%" : "40%"]);
  const yFront = useTransform(scrollYProgress, [0, 1], ["0%", isMobile ? "0%" : "-25%"]);
  const yFloat1 = useTransform(scrollYProgress, [0, 1], [0, isMobile ? 0 : -120]);
  const yFloat2 = useTransform(scrollYProgress, [0, 1], [0, isMobile ? 0 : -220]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden"
    >
      {/* Background parallax layer */}
      <motion.div
        style={{ y: prefersReducedMotion ? 0 : yBg }}
        className="absolute inset-0 dot-grid opacity-60 pointer-events-none"
        aria-hidden="true"
      />
      <motion.div
        style={{ y: prefersReducedMotion ? 0 : yBg }}
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#a68a6d]/5 blur-3xl pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative max-w-8xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div
          style={{ opacity }}
          className="lg:col-span-6 relative z-10"
        >
          <motion.h1
            initial={{ opacity: 0, y: isMobile ? 20 : 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isMobile ? 0.45 : 0.7, delay: 0.1 }}
            data-testid="hero-headline"
            className="font-['Cabinet_Grotesk'] font-black tracking-tighter leading-[0.95] text-[#075a22] text-4xl sm:text-5xl md:text-7xl"
          >
            Deja de perseguir pagos.
            <br />
            <span className="relative inline-block">
              <span className="relative z-10 text-[#a68a6d]">Cobra en automático.</span>
              <motion.span
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.9 }}
                className="absolute -bottom-1 left-0 right-0 h-3 bg-[#a68a6d]/15 origin-left"
                aria-hidden="true"
              />
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: isMobile ? 16 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isMobile ? 0.4 : 0.7, delay: 0.15 }}
            className="mt-5 md:mt-7 text-lg md:text-2xl leading-relaxed text-[#111111]/85 max-w-2xl"
          >
            Kubo Payments automatiza tus cobros recurrentes, recupera pagos fallidos y genera facturación electrónica al instante. Tú te enfocas en crecer; nosotros cobramos por ti.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: isMobile ? 16 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isMobile ? 0.4 : 0.7, delay: 0.2 }}
            className="mt-7 md:mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <button
              onClick={() => { ctaClick("hero_primary", "hero"); onDemoClick(); }}
              data-testid="hero-cta-primary"
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#a68a6d] hover:bg-[#8f7659] text-white text-lg font-bold px-8 py-5 rounded-full transition-all hover:scale-[1.03] shadow-[0_16px_40px_rgba(166,138,109,0.3)]"
            >
              Agendar demo
              <ArrowUpRight
                className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={2.5}
              />
            </button>
            <a
              href="http://app.kubopayments.com/register"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-register"
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#111111] hover:bg-[#2a2a2a] text-white text-lg font-bold px-8 py-5 rounded-full transition-all hover:scale-[1.03] shadow-[0_16px_40px_rgba(17,17,17,0.3)]"
            >
              Registro gratuito
              <ArrowUpRight
                className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={2.5}
              />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: isMobile ? 0.4 : 0.7, delay: 0.25 }}
            className="mt-5 md:mt-6 flex flex-col items-start sm:items-center gap-2 text-sm font-medium text-[#111111]/75"
          >
            <p className="text-[#111111]/85">
              La comisión del 2% aplica solo a pagos online procesados por Kubo.
            </p>
            <a
              href="https://wa.me/18299278886"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp"
              className="inline-flex items-center gap-1.5 text-[#a68a6d] hover:text-[#8f7659] font-medium transition-colors"
            >
              ¿Prefieres escribirnos? WhatsApp: +1 (829) 927‑8886
            </a>
          </motion.div>
        </motion.div>

        {/* Right side: floating screenshots with parallax */}
        <div className="lg:col-span-6 relative h-[300px] sm:h-[460px] md:h-[560px] lg:h-[600px] max-w-2xl w-full lg:max-w-none mx-auto">
          <motion.div
            style={{ y: prefersReducedMotion ? 0 : yFront }}
            initial={{ opacity: 0, x: isMobile ? 20 : 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: isMobile ? 0.5 : 0.9, delay: 0.2 }}
            className="absolute top-0 right-0 w-[85%] rounded-2xl overflow-hidden bg-white border border-[#111111]/10 shadow-[0_30px_60px_-15px_rgba(17,17,17,0.2)] md:shadow-[0_40px_80px_-20px_rgba(17,17,17,0.25)]"
          >
            <RotatingImage
              config={MEDIA.hero.main}
              dataTestId="hero-image-main"
              eagerLoad
            />
          </motion.div>

          <motion.div
            style={{ y: prefersReducedMotion ? 0 : yFloat1 }}
            initial={{ opacity: 0, y: isMobile ? 20 : 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isMobile ? 0.5 : 0.9, delay: 0.35 }}
            className="absolute -bottom-4 -left-4 md:left-0 w-[55%] rounded-xl overflow-hidden bg-white border border-[#111111]/10 shadow-[0_20px_40px_-12px_rgba(17,17,17,0.25)] md:shadow-[0_30px_60px_-15px_rgba(17,17,17,0.3)]"
          >
            <RotatingImage
              config={MEDIA.hero.floating}
              dataTestId="hero-image-floating"
              eagerLoad
            />
          </motion.div>

          <motion.div
            style={{ y: prefersReducedMotion ? 0 : yFloat2 }}
            initial={{ opacity: 0, scale: isMobile ? 0.95 : 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: isMobile ? 0.5 : 0.9, delay: 0.5 }}
            className="absolute top-32 md:top-40 -left-4 md:-left-4 bg-white border border-[#111111]/10 rounded-2xl px-4 md:px-5 py-3 md:py-4 shadow-[0_16px_32px_-8px_rgba(17,17,17,0.18)] md:shadow-[0_20px_40px_-10px_rgba(17,17,17,0.2)]"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-emerald-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <div className="text-xs text-[#111111]/60 font-medium">
                  Cobro exitoso
                </div>
                <div className="text-sm font-bold text-[#111111] font-['Cabinet_Grotesk']">
                  RD$ 2,450.00
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}